import Vue from 'vue'
import Vuex from 'vuex'
// Misma instancia global que main.js registra con interceptores
import axios from 'axios'
import attributeTable from "@/store/modules/attributeTable.js";
import toast from "@/store/modules/toast.js";
import draw from "@/store/modules/draw.js";
import terrain from "@/store/modules/terrain.js";
import symbology from "@/store/modules/symbology.js";
import savedFilters from "@/store/modules/savedFilters.js";
import { MAP_TOOLS, PROTECTED_MAP_TOOLS } from "@/components/mapControls/mapToolsCatalog.js";

// Herramientas que compiten por la interacción del mapa (singleclick / Draw).
// Solo la última activada de esta lista responde a los clics (maptools_hotfix).
const INTERACTION_MAP_TOOLS = ['select', 'geoprocessing', 'annotations', 'draw']

// Deja `tool` como la más recientemente activada, sin duplicarla en el stack.
function promoteMapInteraction(state, tool) {
  if (!INTERACTION_MAP_TOOLS.includes(tool)) return
  state.mapInteractionStack = state.mapInteractionStack
    .filter(item => item !== tool)
    .concat(tool)
}

Vue.use(Vuex)

export default new Vuex.Store({
  modules: {
    attributeTable,
    toast,
    draw,
    terrain,
    symbology,
    savedFilters
  },
  state: {
    role: undefined,
    token: null,
    mobile: false,
    mustChangePassword: false,
    userRegion: '',
    // Identidad del usuario autenticado (menú Cuenta); caché de una sola carga
    currentUser: null,

    // TRUE si se está mostrando un mapa, FALSE en caso contrario
    mapActive: false,

    // Almacena el estado de los controles de navegación y otros elementos del mapa
    mapControls: {
      legends: { active: false },
      loadScreen: { active: false },
      navDrawer: { active: false }
    },

    // Almacena la herramienta seleccionada del mapa
    mapTools: {
      select: { active: false, action: '', data: {} },
      measures: { active: false, action: '', data: {} },
      chart: { active: false, action: '', data: {} },
      magnify: { active: false, action: '' },
      export: { active: false, action: '', format: 'image', data: null },
      layers: { active: false, action: '' },
      search: { active: false, action: '' },
      geoprocessing: { active: false, action: '', data: {} },
      basemap: { active: false, action: '' },
      shpKmzUpload: { active: false, action: '' },
      wmsUpload: { active: false, action: '' },
      help: { active: false, action: '' },
      annotations: { active: false, action: '', data: {} },
      attributesTable: { active: false, action: '', data: {} },
      locator: { active: false, action: '' },
      draw: {active: false, action:'', data:{}},
      print: { active: false, action: '', data: {} }
    },

    // Orden de activación de INTERACTION_MAP_TOOLS: la última que siga activa
    // es la que responde a la interacción del mapa (maptools_hotfix).
    mapInteractionStack: [],

    // Herramientas del visor habilitadas para el mapa actual (QA-11).
    // null = mapa sin configurar o lista aún no cargada: todas disponibles.
    enabledMapTools: null,

    layers: [],
    importedLayers: [],
    activeLayers: [],
    // Capas vulnerables agregadas al mapa por un resultado de geoproceso, no
    // por el usuario en Lista de Capas: participan en la herramienta Consulta
    // igual que activeLayers, pero por separado para no confundir el origen.
    geoprocessResultLayers: [],
    // Capas y geometría de recorte que pide el panel de resultados de
    // geoproceso actualmente abierto, indexado por id de capa. Permite que
    // Lista de Capas sepa, al apagar/prender una capa, si un geoproceso
    // activo todavía la necesita y con qué área de influencia.
    geoprocessLayerRequests: {},
    wmsSessionLayers: [],
    wmsSessionIds: {},
    geoprocessings: []
  },
  getters: {
    backendurl() {
      //Acceder de la siguiente forma:
      //this.$store.getters.backendurl
      return process.env.VUE_APP_API_URL;
    },
    getPermissions(state) {
      if (state.role) {
        return state.role;
      }
      return undefined
    },
    getToken(state) {
      if (state.token) {
        return state.token;
      }
      return undefined
    },
    // TRUE si se trata de un dispositivo móvil, FALSE si es un computador
    isMobile(state) {
      return state.mobile
    },
    isMapActive(state) {
      return state.mapActive
    },
    // Entrega la herramienta del mapa seleccionada
    // Exclude 'select' — SelectTool is rendered independently in MapContainer
    // Exclude 'help' — HelpDocuments is rendered independently in ToolContainer
    getMapSelectedTool: state => {
      let result = Object.keys(state.mapTools)
        .filter(tool => tool !== 'select' && tool !== 'help' && state.mapTools[tool].active)
      return result.length > 0 ? result[0] : ''
    },
    // Entrega la herramienta que controla la interacción del mapa: la última
    // activada que siga activa. '' si ninguna de las cuatro está activa.
    // Se filtra por `active` en vez de mantener el stack al desactivar porque
    // resetMapToolsSelection* (store.js:247-283) escribe `active` directo sin
    // pasar por setMapToolSelection; así el getter no queda nunca desfasado.
    getMapInteractionTool: state => {
      const active = state.mapInteractionStack.filter(tool => state.mapTools[tool].active)
      return active.length > 0 ? active[active.length - 1] : ''
    },
    // Entrega la herramienta que está ejecutando una acción
    getMapActionTool: state => {
      let result = Object.keys(state.mapTools).filter(tool => state.mapTools[tool].action != '')
      let result2 = result.length > 0 ? result[0] : ''
      return result2
    },
    // Entrega los atributos de una herramienta del mapa
    getMapTools: (state) => {
      return state.mapTools
    },
    // Entrega los atributos de un control de navegación del mapa
    getMapControls: (state) => {
      return state.mapControls
    },
    // Entrega las capas disponibles para el usuario del visor 
    getLayers: state => {
      return state.layers
    },
    getImportedLayers: state => {
      return state.importedLayers
    },
    // Entrega las capas seleccionadas para visualizarse en el mapa
    getActiveLayers: state => {
      return state.activeLayers
    },
    // Capas vulnerables agregadas al mapa por un resultado de geoproceso
    getGeoprocessResultLayers: state => {
      return state.geoprocessResultLayers
    },
    // Si el geoproceso activo pide la capa idLayer, entrega { layer, filterGeometry }; si no, null
    getGeoprocessLayerRequest: state => idLayer => {
      return state.geoprocessLayerRequests[idLayer] || null
    },
    // Entrega las capas WMS agregadas por URL durante la sesión (no persistidas)
    getWmsSessionLayers: state => {
      return state.wmsSessionLayers
    },
    // Capas que están esperando datos ahora mismo, para avisarlo sobre el mapa
    getLoadingLayers: state => {
      return state.activeLayers.concat(state.wmsSessionLayers).filter(l => l.loading)
    },
    // Entrega los geoprocesos disponibles para el usuario del visor
    getGeoprocessings: state => {
      return state.geoprocessings
    },
    //Entrega los documentos disponibles para el usuario del visor
    getDocuments: state => {
      return state.docs
    },
    // Región del usuario logueado ('' si no tiene una asignada)
    getUserRegion: state => {
      return state.userRegion
    },
    // Identidad del usuario autenticado para la UI (null hasta la primera carga)
    getCurrentUser: state => {
      return state.currentUser
    },
  },
  mutations: {
    setPermissions(state) {
      // La identidad cacheada deja de ser válida al re-evaluar la sesión
      // (login de otro usuario, logout o expiración)
      state.currentUser = null;
      const name = "session_public" + "=";
      const decodedCookie = decodeURIComponent(document.cookie);
      const ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          const parsed = JSON.parse(c.substring(name.length, c.length));
          state.mustChangePassword = !!parsed.must_change_password;
          state.userRegion = parsed.region || '';
          const role = Object.assign({}, parsed);
          delete role.must_change_password;
          delete role.region;
          delete role.encuadre_regional_activo;
          state.role = role;
          return;
        }
      }
      state.role = undefined;
      state.mustChangePassword = false;
      state.userRegion = '';
      return;
    },
    setMustChangePassword(state, value) {
      state.mustChangePassword = !!value;
    },
    setCurrentUser(state, user) {
      state.currentUser = user;
    },
    setToken(state) {
      const name = "session_token" + "=";
      const decodedCookie = decodeURIComponent(document.cookie);
      const ca = decodedCookie.split(';');
      for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
          c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
          state.token = c.substring(name.length, c.length);
          return;
        }
      }
      state.token = undefined;
      return;
    },
    setIsMobile(state) {
      state.mobile = true;
      return
    },
    setIsNotMobile(state) {
      state.mobile = false;
      return
    },
    setMapActive(state, isActive) {
      state.mapActive = isActive
    },
    // Desactiva cualquier herramienta del mapa seleccionada anteriormente
    resetMapToolsSelection(state) {
      state.mapTools.select.active = false
      state.mapTools.measures.active = false
      state.mapTools.chart.active = false
      state.mapTools.magnify.active = false
      state.mapTools.layers.active = false
      state.mapTools.search.active = false
      state.mapTools.geoprocessing.active = false
      state.mapTools.export.active = false
      state.mapTools.basemap.active = false
      state.mapTools.shpKmzUpload.active = false
      state.mapTools.wmsUpload.active = false
      state.mapTools.help.active = false
      state.mapTools.annotations.active = false
      state.mapTools.attributesTable.active = false
      state.mapTools.locator.active = false
      state.mapTools.draw.active = false
      state.mapTools.print.active = false
    },
    // Desactiva todas las herramientas EXCEPTO la selección de puntos
    resetMapToolsSelectionExceptSelect(state) {
      state.mapTools.measures.active = false
      state.mapTools.chart.active = false
      state.mapTools.magnify.active = false
      state.mapTools.layers.active = false
      state.mapTools.search.active = false
      state.mapTools.geoprocessing.active = false
      state.mapTools.export.active = false
      state.mapTools.basemap.active = false
      state.mapTools.shpKmzUpload.active = false
      state.mapTools.annotations.active = false
      state.mapTools.attributesTable.active = false
      state.mapTools.locator.active = false
      state.mapTools.draw.active = false
      state.mapTools.print.active = false
      // state.mapTools.select is intentionally NOT reset here
      // state.mapTools.help is intentionally NOT reset here (panel concurrente con otras herramientas)
    },
    // Selecciona una herramienta del mapa
    setMapToolSelection(state, payload) {
      console.log("Se recepciona setMapToolSelection", payload)
      // QA-11: bloquear la ACTIVACIÓN de herramientas del catálogo deshabilitadas
      // para el mapa. Acá y no solo en el menú, porque hay entradas laterales
      // (mini-mapa overview → basemap, botones de ayuda dentro de paneles).
      // Nunca se bloquea una desactivación.
      if (
        payload.active &&
        state.enabledMapTools &&
        MAP_TOOLS.some(tool => tool.id === payload.tool) &&
        !state.enabledMapTools.includes(payload.tool) &&
        !PROTECTED_MAP_TOOLS.includes(payload.tool)
      ) {
        console.log(`[store] Herramienta '${payload.tool}' deshabilitada en este mapa, se ignora la activación`)
        return
      }
      state.mapTools[payload.tool].active = payload.active
      // Abrir un panel de las cuatro lo vuelve la herramienta prioritaria.
      // Va después del gate QA-11: una activación bloqueada no toma prioridad.
      if (payload.active) promoteMapInteraction(state, payload.tool)
    },
    // Marca una herramienta ya abierta como la que controla la interacción del mapa
    setMapInteractionTool(state, tool) {
      promoteMapInteraction(state, tool)
    },
    // Suelta la reclamación de una herramienta sobre la interacción del mapa
    // sin cerrar su panel (a diferencia de resetMapToolsSelection*): la deja
    // fuera de la disputa por el clic para que la siguiente activa en el
    // stack (o ninguna) vuelva a responder.
    releaseMapInteraction(state, tool) {
      state.mapInteractionStack = state.mapInteractionStack.filter(item => item !== tool)
    },
    // Fija las herramientas habilitadas del mapa actual (QA-11)
    setEnabledMapTools(state, tools) {
      state.enabledMapTools = tools
    },
    // Elimina cualquier acción realizada por la herramienta
    resetMapToolsAction(state) {
      state.mapTools.select.action = ''
      state.mapTools.measures.action = ''
      state.mapTools.chart.action = ''
      state.mapTools.magnify.action = ''
      state.mapTools.layers.action = ''
      state.mapTools.search.action = ''
      state.mapTools.geoprocessing.action = ''
      state.mapTools.export.action = ''
      state.mapTools.basemap.action = ''
      state.mapTools.shpKmzUpload.action = ''
      state.mapTools.help.action = ''
      state.mapTools.annotations.action = ''
      state.mapTools.attributesTable.action = ''
      state.mapTools.locator.action = ''
      state.mapTools.print.action = ''
    },
    // Ejecuta una acción en una herramienta del mapa
    setMapToolAction(state, payload) {
      console.log("payload data", payload.data)
      console.log("State", state)
      console.log("Action", payload.action)
      state.mapTools[payload.tool].action = payload.action
      state.mapTools[payload.tool].data = payload.data
    },
    // Establece la lista de capas disponibles para el usuario
    setLayers(state, layers) {
      state.layers = layers
    },
    setImportedLayers(state, layers) {
      state.importedLayers = layers || []
    },
    upsertImportedLayer(state, layer) {
      const index = state.importedLayers.findIndex(item => item.id === layer.id)
      if (index === -1) state.importedLayers.push(layer)
      else state.importedLayers.splice(index, 1, layer)
    },
    removeImportedLayer(state, layerId) {
      const index = state.importedLayers.findIndex(item => item.id === layerId)
      if (index > -1) state.importedLayers.splice(index, 1)
    },
    // Agrega una nueva capa a la lista de capas seleccionadas por el usuario para visualizarse en el mapa
    addActiveLayer(state, layer) {
      if (!state.activeLayers.find(l => l.id === layer.id)) {
        state.activeLayers.push(layer)
        console.log('add layer to map', layer)
      } else {
        console.warn('⚠️ La capa ya está activa en el store:', layer.name);
      }
    },
    // Remueve una capa de la lista de capas seleccionadas por el usuario para visualizarse en el mapa
    removeActiveLayer(state, layer) {
      let index = state.activeLayers.findIndex(function (item) { return item.id == layer.id })
      if (index > -1) {
        state.activeLayers.splice(index, 1);
      }
    },
    // Establece la lista de geoprocesos disponibles para el usuario
    setGeoprocessings(state, geoprocessings) {
      state.geoprocessings = geoprocessings
    },
    // Selecciona un control del mapa
    setMapControlSelection(state, payload) {
      state.mapControls[payload.control].active = payload.active
    },
    cleanActiveLayers(state) {
      state.activeLayers = []
    },
    // Agrega una capa vulnerable de resultado de geoproceso
    addGeoprocessResultLayer(state, layer) {
      if (!state.geoprocessResultLayers.find(l => l.id === layer.id)) {
        state.geoprocessResultLayers.push(layer)
      }
    },
    // Remueve una capa vulnerable de resultado de geoproceso
    removeGeoprocessResultLayer(state, layer) {
      let index = state.geoprocessResultLayers.findIndex(function (item) { return item.id == layer.id })
      if (index > -1) {
        state.geoprocessResultLayers.splice(index, 1);
      }
    },
    // Reemplaza el registro completo de capas que pide el geoproceso activo
    // (solo puede haber un panel de resultados abierto a la vez)
    setGeoprocessLayerRequests(state, requests) {
      state.geoprocessLayerRequests = requests
    },
    // Agrega una capa WMS de sesión (agregada por URL, no persistida)
    addWmsSessionLayer(state, layer) {
      if (!state.wmsSessionLayers.find(l => l.id === layer.id)) {
        state.wmsSessionLayers.push(layer)
      }
    },
    // Remueve una capa WMS de sesión
    removeWmsSessionLayer(state, layer) {
      let index = state.wmsSessionLayers.findIndex(function (item) { return item.id == layer.id })
      if (index > -1) {
        state.wmsSessionLayers.splice(index, 1);
      }
    },
    setWmsSessionId(state, { layerId, sessionId }) {
      Vue.set(state.wmsSessionIds, layerId, sessionId)
    },
    clearWmsSessionId(state, layerId) {
      Vue.delete(state.wmsSessionIds, layerId)
    },
    // Estado de carga de una capa mientras espera datos: features en las vectoriales
    // externas (WFS/Esri REST) o la imagen del servidor en las ráster del catálogo.
    // También actualiza la instancia del catálogo: al apagar una capa mientras todavía
    // se está activando, deja de estar en activeLayers, pero su fila debe poder apagar
    // inmediatamente el indicador "Cargando…".
    setLayerLoadState(state, { layerId, loading, progress }) {
      const catalogLayers = (state.layers || []).reduce((result, category) => {
        return result.concat((category && category.layers) || [])
      }, [])
      const layers = state.activeLayers
        .concat(state.wmsSessionLayers, state.importedLayers, catalogLayers)
      const updated = new Set()
      layers
        .filter(l => l && String(l.id) === String(layerId) && !updated.has(l))
        .forEach(layer => {
          updated.add(layer)
          Vue.set(layer, 'loading', loading)
          Vue.set(layer, 'progress', progress)
        })
    }
  },
  actions: {
    // Identidad del usuario autenticado para la UI (menú Cuenta). Una sola
    // carga por sesión; falla en silencio: el bloque simplemente no se muestra
    fetchCurrentUser({ commit, state, getters }) {
      if (state.currentUser) return;
      return axios.get(getters.backendurl + 'api/v1/accounts/me')
        .then((response) => {
          commit('setCurrentUser', response.data);
        })
        .catch(() => {});
    },
    // Desactiva todas las herramientas de mapa
    resetMapToolsSelection({ commit }) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          commit('resetMapToolsSelection')
          resolve()
        }, 1) // Delay mínimo para asegurar que la siguiente acción se ejecute después de esta
      })
    },
    // Selecciona una herramienta en el mapa
    setMapToolSelection({ commit }, payload) {
      commit('setMapToolSelection', payload)
    },
    // Fija las herramientas habilitadas del mapa actual (QA-11)
    setEnabledMapTools({ commit }, tools) {
      commit('setEnabledMapTools', tools)
    },
    // Elimina todas las acciones ejecutadas anteriormente
    resetMapToolsAction({ commit }) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          commit('resetMapToolsAction')
          resolve()
        }, 1) // Delay mínimo para asegurar que la siguiente acción se ejecute después de esta
      })
    },
    // Ejecuta una acción en una herramienta
    setMapToolAction({ dispatch, commit }, payload) {
      return dispatch('resetMapToolsAction').then(() => {
        commit('setMapToolAction', payload)
      })
    },
    // Establece la lista de capas disponibles para el usuario
    setLayers(context, layers) {
      context.commit('setLayers', layers)
    },
    setImportedLayers(context, layers) {
      context.commit('setImportedLayers', layers)
    },
    upsertImportedLayer(context, layer) {
      context.commit('upsertImportedLayer', layer)
    },
    removeImportedLayer(context, layerId) {
      context.commit('removeImportedLayer', layerId)
    },
    // Agrega una nueva capa a la lista de capas seleccionadas por el usuario
    addActiveLayer(context, layer) {
      context.commit('addActiveLayer', layer)
    },
    // Elimina una capa de la lista de capas seleccionadas por el usuario
    removeActiveLayer(context, layer) {
      context.commit('removeActiveLayer', layer)
    },
    // Agrega una capa vulnerable de resultado de geoproceso
    addGeoprocessResultLayer(context, layer) {
      context.commit('addGeoprocessResultLayer', layer)
    },
    // Elimina una capa vulnerable de resultado de geoproceso
    removeGeoprocessResultLayer(context, layer) {
      context.commit('removeGeoprocessResultLayer', layer)
    },
    // Reemplaza el registro de capas que pide el geoproceso activo
    setGeoprocessLayerRequests(context, requests) {
      context.commit('setGeoprocessLayerRequests', requests)
    },
    // Establece la lista de geoprocesos disponibles para el usuario
    setGeoprocessings(context, geoprocessings) {
      context.commit('setGeoprocessings', geoprocessings)
    },
    // Selecciona un control del mapa
    setMapControlSelection(context, payload) {
      context.commit('setMapControlSelection', payload)
    },
    // Reiniciar el estado del mapa
    resetMap(context) {
      context.commit('resetMapToolsSelection')
      context.commit('resetMapToolsAction')
    },
    // Selecciona un control del mapa
    cleanActiveLayers(context) {
      context.commit('cleanActiveLayers')
    },
    // Agrega una capa WMS de sesión
    addWmsSessionLayer(context, layer) {
      context.commit('addWmsSessionLayer', layer)
    },
    // Remueve una capa WMS de sesión
    removeWmsSessionLayer(context, layer) {
      context.commit('removeWmsSessionLayer', layer)
    },
    // Actualiza el estado de carga (loading/progress) de una capa, sea del catálogo o de sesión
    setLayerLoadState(context, payload) {
      context.commit('setLayerLoadState', payload)
    },
    // Nombre anterior, mantenido porque los conectores externos ya lo usan
    setWmsLayerLoadState(context, payload) {
      context.commit('setLayerLoadState', payload)
    },
  }
})
